import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  MenuItem,
  Grid
} from '@mui/material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';

const MaintenanceReleaseCertificate = ({ open, onClose, workOrder, onSuccess }) => {
  const [certificateType, setCertificateType] = useState('MAINTENANCE_RELEASE');
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { showToast } = useToast();

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      await axios.post(`/api/maintenance/${workOrder._id}/release`, {
        certificateType,
        remarks: remarks.trim() || undefined
      });

      showToast('Maintenance release certificate issued successfully', 'success');
      onSuccess();
      handleClose();
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to issue maintenance release certificate';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCertificateType('MAINTENANCE_RELEASE');
    setRemarks('');
    setError('');
    onClose();
  };

  // Check if work order can have certificate issued
  const canIssueCertificate = () => {
    if (!workOrder) return false;
    if (workOrder.status !== 'COMPLETED') return false;
    if (!workOrder.mroEngineerCompleted || !workOrder.mroQmCompletionAcknowledged || !workOrder.camoQmCompletionAcknowledged) return false;
    
    // Check for unresolved discrepancies
    if (workOrder.discrepancies && workOrder.discrepancies.length > 0) {
      const unresolved = workOrder.discrepancies.filter(d => !d.resolved);
      if (unresolved.length > 0) return false;
    }
    
    return true;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Issue Maintenance Release Certificate</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Work Order: <strong>{workOrder?.title}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Aircraft: <strong>{workOrder?.aircraftId?.registration || 'Unknown'}</strong>
          </Typography>
        </Box>

        {!canIssueCertificate() && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {workOrder?.status !== 'COMPLETED' && 'Work order must be in COMPLETED status.'}
            {workOrder?.status === 'COMPLETED' && (!workOrder?.mroEngineerCompleted || !workOrder?.mroQmCompletionAcknowledged || !workOrder?.camoQmCompletionAcknowledged) && 
              'Both MRO_ENGINEER completion and CAMO_QM acknowledgment are required.'}
            {workOrder?.discrepancies && workOrder.discrepancies.filter(d => !d.resolved).length > 0 && 
              `All discrepancies must be resolved. ${workOrder.discrepancies.filter(d => !d.resolved).length} unresolved discrepancy/discrepancies found.`}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              select
              label="Certificate Type"
              value={certificateType}
              onChange={(e) => setCertificateType(e.target.value)}
              required
            >
              <MenuItem value="MAINTENANCE_RELEASE">Maintenance Release</MenuItem>
              <MenuItem value="INSPECTION_RELEASE">Inspection Release</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Remarks (Optional)"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Add any remarks for the certificate..."
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading || !canIssueCertificate()}
        >
          {loading ? 'Issuing...' : 'Issue Certificate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MaintenanceReleaseCertificate;

